/* eslint-disable camelcase */
import { useMemo } from 'react';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useFormContext } from 'react-hook-form';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { SendingDatePicker } from 'src/modules/sending/components/sendingDatePicker';
import { toCustomerSendDate } from 'src/modules/sending/functions/customerSendDate';
import { SendingReminderTimeline } from 'src/modules/sending/components/sendingReminderTimeline';
import { useSendingWizard } from 'src/modules/sending/contexts/sendingWizardContext';
import { SendingOrder } from 'src/modules/sending/models/sendingOrder';

interface SendingWizardStepReminderDateProps {
  order: SendingOrder;
  which: 1 | 2;
  // En beställd påminnelse visas i läsläge: datumen står som text i stället för som väljare.
  readOnly?: boolean;
}

// En påminnelse får gå ut tidigast så här många dagar efter föregående utskicks förfallodag – tidigare datum
// visas inte alls (fakturan har inte hunnit bli obetald i praktiken).
const MIN_DAYS_AFTER_DUE = 11;
// Påminnelsens egna förfallodagar (kortare än huvudutskickets ~35).
const SUGGESTED_PAY_DAYS = 14;
// Hur långt fram i tiden det är meningsfullt att boka en påminnelse, räknat från föregående utskick.
// Datum längre bort visas inte alls – de hör inte till den här aviseringen.
const MAX_MONTHS_AFTER_PREVIOUS = 4;

const fmt = (d: Dayjs | null): string => (d ? d.format('D MMM YYYY') : '–');

/**
 * Första steget när en påminnelse beställs: när ska den skickas och när förfaller den? Tidslinjen visar
 * föregående utskick och dess förfallodatum, så det är lätt att se att påminnelsen landar rimligt efter
 * att fakturan faktiskt förfallit.
 */
export const SendingWizardStepReminderDate = ({ order, which, readOnly = false }: SendingWizardStepReminderDateProps) => {
  const { watch, setValue } = useFormContext();
  const { sendDate, setSendDate } = useSendingWizard();

  const payDateRaw = watch('payDate') as string | undefined;
  const payDate = payDateRaw ? dayjs(payDateRaw) : null;
  const dateId = watch('date_id') as number | undefined;

  // Föregående led: huvudutskicket för Påminnelse 1, Påminnelse 1 för Påminnelse 2.
  const previousSendDate = useMemo(() => {
    const raw = which === 2 ? order.reminder1_date?.sndDate : order.date?.sndDate;
    return raw ? dayjs(raw) : null;
  }, [which, order]);

  const previousPayDate = useMemo(() => {
    const raw = which === 2 ? order.reminder1_payDate : order.payDate;
    return raw ? dayjs(raw) : null;
  }, [which, order]);

  const previousLabel = which === 2 ? 'Påminnelse 1' : 'Huvudutskick';

  // Tidslinjen visar kundens utskicksdatum (DB-datum − 1 dag), precis som datumkorten och headern. Förfallodatum
  // är riktiga datum och räknas om inte.
  const previousSendDisplay = toCustomerSendDate(previousSendDate);
  const sendDateDisplay = toCustomerSendDate(sendDate);

  // Intervallet räknas från föregående utskicks förfallodag: tidigast 11 dagar efter den, och inte längre bort än
  // några månader. Saknas förfallodatum faller vi tillbaka på utskicksdatumet.
  const rangeBase = previousPayDate ?? previousSendDate;
  const earliestSelectable = rangeBase ? rangeBase.add(previousPayDate ? MIN_DAYS_AFTER_DUE : 1, 'day') : null;
  const latestSelectable = rangeBase ? rangeBase.add(MAX_MONTHS_AFTER_PREVIOUS, 'month').endOf('month') : null;

  const sendBeforePrevious = !!(sendDate && previousSendDate && !sendDate.isAfter(previousSendDate, 'day'));
  const payBeforeSend = !!(sendDate && payDate && payDate.isBefore(sendDate, 'day'));

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', '& .MuiGrid-item': { pt: 1.5 } }}>
      <GridWrapper>
        <Grid item xs={12}>
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 0.5 }}>
            {readOnly ? `Så här skickas påminnelse ${which}` : `När ska påminnelse ${which} skickas?`}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {readOnly
              ? 'Påminnelsen är beställd, så datumen är låsta. Kontakta Föreningssupport om något behöver ändras.'
              : 'Välj ett utskicksdatum som ligger en bit efter att fakturan förfallit, och ett nytt förfallodatum för påminnelsen.'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <SendingReminderTimeline
            which={which}
            previousLabel={previousLabel}
            previousSendDate={previousSendDisplay}
            previousPayDate={previousPayDate}
            sendDate={sendDateDisplay}
            payDate={payDate}
          />
        </Grid>

        {readOnly && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 700, letterSpacing: 0.6, display: 'block' }}>
                  Utskicksdatum
                </Typography>
                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  {fmt(sendDateDisplay)}
                </Typography>
              </Box>
              <Box>
                <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 700, letterSpacing: 0.6, display: 'block' }}>
                  Fakturornas förfallodatum
                </Typography>
                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  {fmt(payDate)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}

        {!readOnly && (
          <Grid item xs={12}>
            <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 700, letterSpacing: 0.6, display: 'block', mb: 1 }}>
              Utskicksdatum
            </Typography>
            {previousPayDate && (
              // Namnge inte ett exakt "tidigast"-datum: det är sällan ett av FS produktionsdatum och blir därför
              // förvirrande. Säg regeln i stället – de valbara datumen syns nedanför.
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Påminnelsen måste skickas minst {MIN_DAYS_AFTER_DUE} dagar efter att fakturan förfallit ({fmt(previousPayDate)}). Tidigare datum går
                inte att välja.
              </Typography>
            )}
            <SendingDatePicker
              value={dateId}
              minDate={earliestSelectable}
              maxDate={latestSelectable}
              emptyText={`Det finns inga utskicksdatum mellan ${fmt(earliestSelectable)} och ${fmt(latestSelectable)} att välja på.`}
              onChange={(id, date) => {
                setValue('date_id', id, { shouldValidate: true });
                setSendDate(date);
                // Föreslå förfallodatum = utskick + 14 dagar om det saknas eller hamnar före utskicket.
                if (!payDate || payDate.isBefore(date, 'day')) {
                  setValue('payDate', date.add(SUGGESTED_PAY_DAYS, 'day').format('YYYY-MM-DD'), { shouldValidate: true });
                }
              }}
            />
          </Grid>
        )}

        {!readOnly && sendDate && (
          <Grid item xs={12}>
            <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 700, letterSpacing: 0.6, display: 'block', mb: 1 }}>
              Förfallodatum för påminnelsen
            </Typography>
            <DatePicker
              value={payDate}
              minDate={sendDate ?? undefined}
              onChange={(value) => setValue('payDate', value ? (value as Dayjs).format('YYYY-MM-DD') : '', { shouldValidate: true })}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Grid>
        )}

        {(sendBeforePrevious || payBeforeSend) && (
          <Grid item xs={12}>
            {sendBeforePrevious && (
              <Alert severity='error' sx={{ mb: 1 }}>
                Påminnelsen måste skickas efter {previousLabel.toLowerCase()}et ({fmt(previousSendDate)}).
              </Alert>
            )}
            {payBeforeSend && <Alert severity='error'>Förfallodatumet kan inte ligga före påminnelsens utskicksdatum.</Alert>}
          </Grid>
        )}
      </GridWrapper>
    </Box>
  );
};
